import {
  updatePhiRedactionBoundingBoxesStudiesStudyIdSubjectsSubjectIdTimepointSubmissionsTimepointSubmissionIdPhiRedactionBoundingBoxesPut,
  type UpdatePhiRedactionBoundingBoxesStudiesStudyIdSubjectsSubjectIdTimepointSubmissionsTimepointSubmissionIdPhiRedactionBoundingBoxesPutData,
  type UpdatePhiRedactionBoundingBoxesStudiesStudyIdSubjectsSubjectIdTimepointSubmissionsTimepointSubmissionIdPhiRedactionBoundingBoxesPutResponse,
  type UpdatePHIRedactionBoundingBoxesRequest,
  HTTPValidationError,
} from '@common/config/api/client'
import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query'
import { AxiosError } from 'axios'

/**
 * Hook for updating PHI redaction bounding boxes
 *
 * This hook allows updating the PHI detection boxes for DICOM containers, including:
 * - Adding new PHI boxes (via new_bounding_boxes)
 * - Modifying existing PHI boxes (via edits)
 * - Removing PHI boxes (via redaction_ids_to_remove)
 *
 * After a successful update, it automatically invalidates the PHI redaction status query
 * to ensure the UI displays the latest data.
 */
export const useUpdatePHIRedactionBoxes = (): UseMutationResult<
  UpdatePhiRedactionBoundingBoxesStudiesStudyIdSubjectsSubjectIdTimepointSubmissionsTimepointSubmissionIdPhiRedactionBoundingBoxesPutResponse,
  AxiosError<HTTPValidationError>,
  {
    params: UpdatePhiRedactionBoundingBoxesStudiesStudyIdSubjectsSubjectIdTimepointSubmissionsTimepointSubmissionIdPhiRedactionBoundingBoxesPutData['path']
    data: UpdatePHIRedactionBoundingBoxesRequest
  }
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ params, data }) => {
      const response =
        await updatePhiRedactionBoundingBoxesStudiesStudyIdSubjectsSubjectIdTimepointSubmissionsTimepointSubmissionIdPhiRedactionBoundingBoxesPut(
          {
            path: params,
            body: data,
            throwOnError: true,
          },
        )
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch the PHI redaction status query to get updated data
      queryClient.invalidateQueries({ queryKey: ['PHIRedactionStatus'] })
    },
    retry: 2, // Retry failed mutations up to 2 times
  })
}
